// Utility for scoring calculations
import { RUBRIC_GROUPS, RUBRIC_GROUPS as REAL_RUBRIC_GROUPS } from '../constants/scoring';

/**
 * Calculates the total score for a group based on form values.
 * @param {Object} group - The group object containing items.
 * @param {Object} form - The form state object.
 * @returns {number} The total score for the group.
 */
export function calculateGroupScore(group, form) {
  const items = Array.isArray(group.items) ? group.items : [];
  return items.reduce((sum, _item, idx) => {
    const val = form[`${group.key}_${idx}`];
    return sum + (typeof val === 'number' && val >= 0 ? val : 0);
  }, 0);
}

/**
 * Calculates the overall total score for all groups.
 * @param {Object} rubricGroups - The rubric groups object.
 * @param {Object} form - The form state object.
 * @returns {number} The overall total score.
 */
export function calculateOverallScore(rubricGroups, form) {
  return Object.values(rubricGroups).reduce((total, group) => {
    return total + calculateGroupScore(group, form);
  }, 0);
}

/**
 * Builds field configs for a group for use in ScoringModule.
 * @param {Object} group - The group object containing items.
 * @param {string} groupKey - The group key.
 * @param {Object} form - The form state object.
 * @param {Function} setForm - The form setter function.
 * @param {boolean} disabled - Whether the fields are disabled.
 * @returns {Array} Array of field configs.
 */
export function buildFields(group, groupKey, form, setForm, disabled) {
  return (Array.isArray(group.items) ? group.items : []).map((item, idx) => ({
    label: item.label,
    value: form[`${groupKey}_${idx}`] ?? '',
    onChange: (event) => setForm({ ...form, [`${groupKey}_${idx}`]: event.target.value }),
    disabled,
    selectId: `${groupKey}_${idx}`,
    selectLabelId: `${groupKey}_${idx}_label`,
    options: Array.isArray(item.scores)
      ? item.scores.map((scoreObj) => ({
          value: scoreObj.score,
          label: scoreObj.description,
        }))
      : [],
    style: item.style || {},
  }));
}

const PLANNING_MEETING_1_FIELDS_VERSION_1 = [
  'gradingSheet1Completed',
  'gradingSheet1Submitted',
  'gradingSheet1Clarity',
  'module1Submitted',
  'module1Completed',
  'module2Submitted',
  'module2Completed',
  'module3Submitted',
  'module3Completed',
];
/**
 * Checks if all required scoring fields are present and valid for the given form version.
 * @param {Object} form - The form state object.
 * @returns {boolean} True if all required fields are present and valid, false otherwise.
 */
export function isScoringFieldsValid(form) {
  const isV1 = !form.version || form.version === 1;
  if (isV1) {
    for (const field of PLANNING_MEETING_1_FIELDS_VERSION_1) {
      if (form[field] === undefined) return false;
      if (form[field] < 0) return false;
    }
    return true;
  }
  // assuming version 2 as default version: check all rubric group fields
  for (const group of Object.values(REAL_RUBRIC_GROUPS)) {
    for (let idx = 0; idx < group.items.length; idx++) {
      if (form[`${group.key}_${idx}`] === undefined) return false;
      if (form[`${group.key}_${idx}`] < 0) return false;
    }
  }
  return true;
}

/**
 * Calculates the legacy score for version 1 forms.
 * @param {Object} form - The form state object.
 * @returns {number}
 */
export const calculateLegacyScore = (form) => {
  const { total, score } = PLANNING_MEETING_1_FIELDS_VERSION_1.reduce(
    (acc, field) => {
      const value = form[field];
      acc.total += value || 0;
      acc.score += value > -1 ? value : 0;
      return acc;
    },
    { total: 0, score: 0 }
  );
  return total > 0 ? score : 0;
};

export const getPlanningMeeting1TotalScore = (form) => {
  return !form.version || form.version === 1 ? calculateLegacyScore(form) : calculateOverallScore(RUBRIC_GROUPS, form);
};
